import { useMutation, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface NearbyStore {
  placeId: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
  website: string | null;
  phone: string | null;
  rating: number | null;
  ratingCount: number | null;
  distance: number;
}

export interface NearbyStoresResponse {
  center: { lat: number; lng: number } | null;
  radiusMiles: number;
  stores: NearbyStore[];
}

export function useNearbyStoresSearch() {
  return useMutation({
    mutationFn: async (params: { address?: string; lat?: number; lng?: number; radiusMiles: number }) => {
      const { data, error } = await supabase.functions.invoke<NearbyStoresResponse>('nearby-stores', {
        body: params,
      });
      if (error) throw new Error(error.message);
      if (!data) throw new Error('No response');
      return data;
    },
  });
}

export interface CommunityStore {
  id: string;
  name: string;
  address: string;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  latitude: number;
  longitude: number;
  website: string | null;
  phone: string | null;
  hosts_tournaments: boolean;
  verified: boolean;
  notes: string | null;
  google_place_id: string | null;
}

export function useCommunityStores() {
  return useQuery({
    queryKey: ['community-stores'],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<CommunityStore[]> => {
      const { data, error } = await supabase
        .from('lorcana_stores')
        .select('id,name,address,city,state,postal_code,latitude,longitude,website,phone,hosts_tournaments,verified,notes,google_place_id')
        .order('verified', { ascending: false })
        .order('name');
      if (error) throw error;
      return (data ?? []) as CommunityStore[];
    },
  });
}

export function useStoreBlocklist() {
  return useQuery({
    queryKey: ['store-blocklist'],
    staleTime: 60 * 60 * 1000,
    queryFn: async (): Promise<Set<string>> => {
      const { data, error } = await supabase.from('store_blocklist').select('google_place_id');
      if (error) return new Set();
      return new Set((data ?? []).map(r => r.google_place_id));
    },
  });
}

export function useSubmitStore() {
  return useMutation({
    mutationFn: async (input: {
      name: string;
      address: string;
      latitude: number;
      longitude: number;
      website?: string | null;
      phone?: string | null;
      hosts_tournaments?: boolean;
      google_place_id?: string | null;
      notes?: string | null;
    }) => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user) throw new Error('Sign in to submit a store');
      const { error } = await supabase.from('lorcana_stores').insert({
        ...input,
        submitted_by: auth.user.id,
      });
      if (error) throw error;
    },
  });
}
